import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Truck, Languages, Lock } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `text-[13px] font-medium tracking-wide transition-colors duration-200 ${
      isActive ? "text-[#00D4FF]" : "text-white/70 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050509]/85 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
            <div className="relative w-9 h-9 rounded-lg flex items-center justify-center group-hover:rotate-3 transition-transform overflow-hidden">
              <img src="/wasz-logo.png" alt="Wasz.nl logo" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-extrabold tracking-tight text-base">Wasz<span className="text-[#00D4FF]">.nl</span></div>
              <div className="text-[10px] text-white/40 uppercase tracking-[0.18em] font-medium">Mobile Truckwash</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            <NavLink data-testid="nav-home" to="/" className={linkClass} end>
              {t("nav.home")}
            </NavLink>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-services-trigger"
                className="text-[13px] font-medium tracking-wide text-white/70 hover:text-white inline-flex items-center gap-1 outline-none"
              >
                {t("nav.services")} <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-64 bg-[#0F0F23] border-white/10 text-white"
                align="start"
              >
                <DropdownMenuItem data-testid="nav-mobile-truckwash" asChild>
                  <Link to="/mobiele-truckwash" className="cursor-pointer">{t("nav.mobile_truckwash")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-truck-cleaning" asChild>
                  <Link to="/vrachtwagenreiniging" className="cursor-pointer">{t("nav.truck_cleaning")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-fleet-cleaning" asChild>
                  <Link to="/fleet-cleaning" className="cursor-pointer">{t("nav.fleet_cleaning")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-wagenpark" asChild>
                  <Link to="/wagenpark-reiniging" className="cursor-pointer">{t("nav.wagenpark")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-haccp" asChild>
                  <Link to="/haccp-certificaten" className="cursor-pointer">{t("nav.haccp")}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-locations-trigger"
                className="text-[13px] font-medium tracking-wide text-white/70 hover:text-white inline-flex items-center gap-1 outline-none"
              >
                {t("nav.locations")} <ChevronDown className="w-3.5 h-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-[#0F0F23] border-white/10 text-white" align="start">
                <DropdownMenuItem data-testid="nav-loc-almere" asChild>
                  <Link to="/truckwash-almere" className="cursor-pointer">Truckwash Almere</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-amsterdam" asChild>
                  <Link to="/truckwash-amsterdam" className="cursor-pointer">Truckwash Amsterdam</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-denhaag" asChild>
                  <Link to="/truckwash-den-haag" className="cursor-pointer">Truckwash Den Haag</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-rotterdam" asChild>
                  <Link to="/truckwash-rotterdam" className="cursor-pointer">Truckwash Rotterdam</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-utrecht" asChild>
                  <Link to="/truckwash-utrecht" className="cursor-pointer">Truckwash Utrecht</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-amersfoort" asChild>
                  <Link to="/truckwash-amersfoort" className="cursor-pointer">Truckwash Amersfoort</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-alkmaar" asChild>
                  <Link to="/truckwash-alkmaar" className="cursor-pointer">Truckwash Alkmaar</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-alphen-aan-den-rijn" asChild>
                  <Link to="/truckwash-alphen-aan-den-rijn" className="cursor-pointer">Truckwash Alphen aan den Rijn</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-barneveld" asChild>
                  <Link to="/truckwash-barneveld" className="cursor-pointer">Truckwash Barneveld</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-breda" asChild>
                  <Link to="/truckwash-breda" className="cursor-pointer">Truckwash Breda</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-waddinxveen" asChild>
                  <Link to="/truckwash-waddinxveen" className="cursor-pointer">Truckwash Waddinxveen</Link>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-loc-zeewolde" asChild>
                  <Link to="/truckwash-zeewolde" className="cursor-pointer">Truckwash Zeewolde</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink data-testid="nav-waszbase" to="/waszbase" className={linkClass}>
              {t("nav.waszbase")}
            </NavLink>
            <NavLink data-testid="nav-about" to="/over-ons" className={linkClass}>
              {t("nav.about")}
            </NavLink>
            <NavLink data-testid="nav-blog" to="/blog" className={linkClass}>
              {t("nav.blog")}
            </NavLink>
            <NavLink data-testid="nav-faq" to="/faq" className={linkClass}>
              {t("nav.faq")}
            </NavLink>
          </nav>

          {/* Right: language + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "nl" ? "en" : "nl")}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wider text-white/60 hover:text-[#00D4FF] transition-colors"
              aria-label="Toggle language"
            >
              <Languages className="w-3.5 h-3.5" />
              {lang === "nl" ? "NL / EN" : "EN / NL"}
            </button>
            <a
              href="https://www.waszbase.co.nl/klant/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-klant-login"
            >
              <Button
                variant="outline"
                className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-[#00D4FF] hover:border-[#00D4FF] font-semibold rounded-md text-xs h-9 px-4 inline-flex items-center gap-1.5"
              >
                <Lock className="w-3.5 h-3.5" /> Klant Login
              </Button>
            </a>
            <Link to="/contact?type=quote" data-testid="header-cta">
              <Button className="bg-[#00D4FF] hover:bg-white text-[#050509] font-bold rounded-md text-xs h-9 px-5">
                {t("nav.cta")}
              </Button>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#050509]">
          <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-1 text-white">
            {[
              ["/", t("nav.home")],
              ["/mobiele-truckwash", t("nav.mobile_truckwash")],
              ["/vrachtwagenreiniging", t("nav.truck_cleaning")],
              ["/fleet-cleaning", t("nav.fleet_cleaning")],
              ["/wagenpark-reiniging", t("nav.wagenpark")],
              ["/waszbase", t("nav.waszbase")],
              ["/haccp-certificaten", t("nav.haccp")],
              ["/truckwash-almere", "Truckwash Almere"],
              ["/truckwash-amsterdam", "Truckwash Amsterdam"],
              ["/truckwash-den-haag", "Truckwash Den Haag"],
              ["/truckwash-rotterdam", "Truckwash Rotterdam"],
              ["/truckwash-utrecht", "Truckwash Utrecht"],
              ["/truckwash-amersfoort", "Truckwash Amersfoort"],
              ["/truckwash-alkmaar", "Truckwash Alkmaar"],
              ["/truckwash-alphen-aan-den-rijn", "Truckwash Alphen aan den Rijn"],
              ["/truckwash-barneveld", "Truckwash Barneveld"],
              ["/truckwash-breda", "Truckwash Breda"],
              ["/truckwash-waddinxveen", "Truckwash Waddinxveen"],
              ["/truckwash-zeewolde", "Truckwash Zeewolde"],
              ["/over-ons", t("nav.about")],
              ["/blog", t("nav.blog")],
              ["/faq", t("nav.faq")],
            ].map(([href, label]) => (
              <Link
                key={href}
                to={href}
                className="py-3 text-sm border-b border-white/5 hover:text-[#00D4FF]"
              >
                {label}
              </Link>
            ))}
            <div className="flex items-center gap-3 mt-4">
              <button
                data-testid="lang-toggle-mobile"
                onClick={() => setLang(lang === "nl" ? "en" : "nl")}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wider text-white/60 hover:text-[#00D4FF]"
              >
                <Languages className="w-3.5 h-3.5" />
                {lang === "nl" ? "NL / EN" : "EN / NL"}
              </button>
              <Link to="/contact?type=quote" className="ml-auto">
                <Button className="bg-[#00D4FF] hover:bg-white text-[#050509] font-bold rounded-md text-xs h-9 px-5">
                  {t("nav.cta")}
                </Button>
              </Link>
            </div>
            <a
              href="https://www.waszbase.co.nl/klant/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-klant-login"
              className="mt-3"
            >
              <Button
                variant="outline"
                className="w-full bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-[#00D4FF] hover:border-[#00D4FF] font-semibold rounded-md text-xs h-9 inline-flex items-center justify-center gap-1.5"
              >
                <Lock className="w-3.5 h-3.5" /> Klant Login
              </Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
